import React from "react";

/**
 * Tripex brand visual language — Vertical Elegance.
 * Long, vertical, intertwined ribbons (Emerald, Lavender, Gold, Cream)
 * stretching across the entire canvas on a soft sage gradient base.
 * Minimal, sophisticated, lean.
 */
export default function Backdrop() {
  return (
    <div className="flow-bg" aria-hidden="true">
      {/* Sage/Cream foundation gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 20%, #EAE3D0 50%, #FFFFFF 80%, #FFFFFF 100%)',
        }}
      />
      {/* Subtle sage tint overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 20% 30%, rgba(77,182,162,0.10) 0%, transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(196,153,232,0.10) 0%, transparent 60%), radial-gradient(ellipse at 50% 100%, rgba(205,183,110,0.06) 0%, transparent 60%)',
        }}
      />

      {/* Vertical brand ribbons */}
      <svg
        className="flow-vertical"
        viewBox="0 0 1440 2400"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Emerald flowing top → bottom */}
          <linearGradient id="v-emerald-long" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#A8E1D7" stopOpacity="0" />
            <stop offset="10%" stopColor="#A8E1D7" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#51BDB0" stopOpacity="0.95" />
            <stop offset="90%" stopColor="#2E8B7A" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#2E8B7A" stopOpacity="0" />
          </linearGradient>
          {/* Lavender flowing */}
          <linearGradient id="v-lavender-long" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#E0BFEE" stopOpacity="0" />
            <stop offset="12%" stopColor="#E0BFEE" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#C499E8" stopOpacity="1" />
            <stop offset="90%" stopColor="#9B7BBF" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#9B7BBF" stopOpacity="0" />
          </linearGradient>
          {/* Gold flowing */}
          <linearGradient id="v-gold-long" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#F4E3A6" stopOpacity="0" />
            <stop offset="12%" stopColor="#F4E3A6" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#E5DA97" stopOpacity="0.95" />
            <stop offset="90%" stopColor="#C9BC72" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#C9BC72" stopOpacity="0" />
          </linearGradient>
          {/* Cream/White flowing */}
          <linearGradient id="v-cream-long" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
            <stop offset="20%" stopColor="#FFFFFF" stopOpacity="0.75" />
            <stop offset="50%" stopColor="#FFFCF5" stopOpacity="0.9" />
            <stop offset="80%" stopColor="#FFFFFF" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>

          <filter id="soft" x="-2%" y="-2%" width="104%" height="104%">
            <feGaussianBlur stdDeviation="1.5" />
          </filter>
        </defs>

        {/* Far-left lavender ribbon */}
        <path
          d="M 120 -50 C 200 400, 60 900, 180 1400 S 240 1900, 80 2450"
          stroke="url(#v-lavender-long)"
          strokeWidth="44"
          strokeLinecap="round"
          fill="none"
          filter="url(#soft)"
          opacity="0.55"
        >
          <animate attributeName="d" dur="32s" repeatCount="indefinite"
            values="
              M 120 -50 C 200 400, 60 900, 180 1400 S 240 1900, 80 2450;
              M 140 -50 C 180 420, 100 880, 160 1420 S 220 1880, 100 2450;
              M 120 -50 C 200 400, 60 900, 180 1400 S 240 1900, 80 2450
            " />
        </path>

        {/* Left-center emerald ribbon (long, dominant) */}
        <path
          d="M 360 -50 C 280 360, 440 880, 320 1380 S 460 1880, 380 2450"
          stroke="url(#v-emerald-long)"
          strokeWidth="40"
          strokeLinecap="round"
          fill="none"
          filter="url(#soft)"
          opacity="0.58"
        >
          <animate attributeName="d" dur="28s" repeatCount="indefinite"
            values="
              M 360 -50 C 280 360, 440 880, 320 1380 S 460 1880, 380 2450;
              M 340 -50 C 300 380, 420 860, 340 1400 S 440 1860, 400 2450;
              M 360 -50 C 280 360, 440 880, 320 1380 S 460 1880, 380 2450
            " />
        </path>

        {/* Center cream/white ribbon — soft luminous core */}
        <path
          d="M 600 -50 C 720 400, 540 900, 660 1400 S 540 1900, 620 2450"
          stroke="url(#v-cream-long)"
          strokeWidth="60"
          strokeLinecap="round"
          fill="none"
          filter="url(#soft)"
          opacity="0.45"
        >
          <animate attributeName="d" dur="36s" repeatCount="indefinite"
            values="
              M 600 -50 C 720 400, 540 900, 660 1400 S 540 1900, 620 2450;
              M 620 -50 C 700 420, 560 880, 640 1420 S 560 1880, 600 2450;
              M 600 -50 C 720 400, 540 900, 660 1400 S 540 1900, 620 2450
            " />
        </path>

        {/* Center-right gold ribbon */}
        <path
          d="M 860 -50 C 760 400, 920 920, 800 1400 S 940 1900, 880 2450"
          stroke="url(#v-gold-long)"
          strokeWidth="38"
          strokeLinecap="round"
          fill="none"
          filter="url(#soft)"
          opacity="0.55"
        >
          <animate attributeName="d" dur="30s" repeatCount="indefinite"
            values="
              M 860 -50 C 760 400, 920 920, 800 1400 S 940 1900, 880 2450;
              M 840 -50 C 780 420, 900 900, 820 1420 S 920 1880, 900 2450;
              M 860 -50 C 760 400, 920 920, 800 1400 S 940 1900, 880 2450
            " />
        </path>

        {/* Right lavender mid-ribbon */}
        <path
          d="M 1080 -50 C 1180 380, 1020 880, 1140 1380 S 1020 1900, 1100 2450"
          stroke="url(#v-lavender-long)"
          strokeWidth="42"
          strokeLinecap="round"
          fill="none"
          filter="url(#soft)"
          opacity="0.55"
        >
          <animate attributeName="d" dur="34s" repeatCount="indefinite"
            values="
              M 1080 -50 C 1180 380, 1020 880, 1140 1380 S 1020 1900, 1100 2450;
              M 1100 -50 C 1160 400, 1040 860, 1120 1400 S 1040 1880, 1120 2450;
              M 1080 -50 C 1180 380, 1020 880, 1140 1380 S 1020 1900, 1100 2450
            " />
        </path>

        {/* Far-right emerald accent */}
        <path
          d="M 1320 -50 C 1240 420, 1380 900, 1280 1400 S 1380 1900, 1320 2450"
          stroke="url(#v-emerald-long)"
          strokeWidth="34"
          strokeLinecap="round"
          fill="none"
          filter="url(#soft)"
          opacity="0.5"
        >
          <animate attributeName="d" dur="40s" repeatCount="indefinite"
            values="
              M 1320 -50 C 1240 420, 1380 900, 1280 1400 S 1380 1900, 1320 2450;
              M 1300 -50 C 1260 440, 1360 880, 1300 1420 S 1360 1880, 1340 2450;
              M 1320 -50 C 1240 420, 1380 900, 1280 1400 S 1380 1900, 1320 2450
            " />
        </path>

        {/* Crossing crisp hairlines for refinement */}
        <path d="M 220 -50 C 300 600, 200 1200, 280 1800 S 220 2400, 260 2450" stroke="#51BDB0" strokeWidth="1.2" fill="none" opacity="0.4" />
        <path d="M 1180 -50 C 1100 600, 1200 1200, 1120 1800 S 1180 2400, 1140 2450" stroke="#C499E8" strokeWidth="1.2" fill="none" opacity="0.4" />
      </svg>
    </div>
  );
}
